import {
  Document,
  Page,
  StyleSheet,
  Font,
  View,
  Text,
  Image,
} from "@react-pdf/renderer";

import { ReportProps } from "../types";
import { EmployeeStatus } from "../../../features/process/types/processTypes";
import { getEmployeeStatusDate } from "../../../features/process/utils/statusDate";
import { getAmharicCalendarFormatted } from "../../../utils/calendarUtility";

Font.register({
  family: "Noto",
  format: "truetype",
  fonts: [
    {
      src: "/fonts/Noto_Naskh_Arabic/NotoNaskhArabic-VariableFont_wght.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Regular.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Medium.ttf",
    },
    {
      src: "/fonts/Noto_Naskh_Arabic/static/NotoNaskhArabic-Bold.ttf",
      fontWeight: 600,
    },
  ],
});
Font.register({
  family: "Ethiopic",
  format: "truetype",
  fonts: [
    {
      src: "/fonts/Noto_Sans_Ethiopic/NotoSansEthiopic-VariableFont_wght.ttf",
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Regular.ttf",
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Medium.ttf",
    },
    {
      src: "/fonts/Noto_Sans_Ethiopic/static/NotoSansEthiopic-Bold.ttf",
      fontWeight: 600,
    },
  ],
});
Font.register({
  family: "Open Sans",
  format: "truetype",
  fonts: [
    {
      src: "/fonts/open-sans-regular.ttf",
    },
    {
      src: "/fonts/open-sans-600.ttf",
      fontWeight: 600,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontSize: 11,
    paddingTop: 0,
    paddingLeft: 20,
    paddingRight: 20,
    lineHeight: 1.5,
    flexDirection: "column",
  },

  row2: {
    flexDirection: "row",
    alignItems: "center",
    height: 30,
  },
  row21: {
    borderColor: "#000000",
    borderWidth: 2,
    alignItems: "center",
    marginRight: -1,
    borderRightWidth: 0,
  },
  row31: {
    width: "25%",
    borderColor: "#000000",
    borderWidth: 1,
    alignItems: "center",
    marginRight: -1,
    borderTopWidth: 0,
  },
  row3: {
    flexDirection: "row",
    height: 30,
  },
  cellCenter: {
    textAlign: "center",
    margin: 0,
    padding: 0,
    fontWeight: "medium",
    fontFamily: "Ethiopic",
    fontSize: "14px",
  },
  headerCenter: {
    textAlign: "center",
    margin: 0,
    padding: 0,
    fontWeight: "bold",
    fontFamily: "Ethiopic",
    fontSize: "14px",
  },
  header: {
    position: "absolute",
    top: 20,
    left: 0,
    right: 0,
    height: "150px",
    marginBottom: 20,
  },
  dateHeader: {
    textAlign: "left",
    margin: 0,
    padding: 0,
    fontWeight: "bold",
    fontFamily: "Ethiopic",
    fontSize: "14px",
  },
});

const SUBJECT_LINE: Partial<Record<EmployeeStatus, string>> = {
  [EmployeeStatus.departed]: `ጉዳዩ: ተጉዘው በስራ ላይ ያሉ ሰራተኞች ዝርዝር`,
  [EmployeeStatus.lost]: `ጉዳዩ: ተጉዘው ከሚሰሩበት ቤት የጠፉ ሰራተኞች ዝርዝር`,
  [EmployeeStatus.returned]: `ጉዳዩ: ኮንትራት ሳይጨርሱ የተመለሱ ሰራተኞች ዝርዝር`,
  [EmployeeStatus.contract_completed]: `ጉዳዩ: ኮንትራት ጨርሰው የተመለሱ ሰራተኞች ዝርዝር`,
  [EmployeeStatus.discontinued]: `ጉዳዩ: ኢሹድ ተብልው ፕሮሰስ ያቋረጡ ሰራተኞች ዝርዝር`,
};

const DATE_COLUMN_HEADER: Partial<Record<EmployeeStatus, string>> = {
  [EmployeeStatus.departed]: `የተጓዙበት ቀን`,
  [EmployeeStatus.lost]: `የጠፉበት ቀን`,
  [EmployeeStatus.returned]: `የተመለሱበት ቀን`,
  [EmployeeStatus.contract_completed]: `የተጠናቀቀበት ቀን`,
  [EmployeeStatus.discontinued]: `ያቋረጡበት ቀን`,
};

function getBodyText(
  reportType: EmployeeStatus | undefined,
  durationStartDate: string | undefined,
  durationEndDate: string | undefined,
): string {
  const range = `ከ${durationStartDate} እስከ ${durationEndDate}`;
  switch (reportType) {
    case EmployeeStatus.departed:
      return `በዚሁም መሰረት ስማቸው ከታች የተዘረዘሩ በድርጅታችን አማካኝነት ${range} ተጉዘው በስራ ላይ ያሉ መሆኑን እናሳውቃለን ::`;
    case EmployeeStatus.lost:
      return `በዚሁም መሰረት ስማቸው ከታች የተዘረዘሩ ተጓዦች ${range} ከሚሰሩብት ቤት መጥፋታቸውን እናሳውቃለን::`;
    case EmployeeStatus.returned:
      return `በዚሁም መሰረት ስማቸው ከታች የተዘረዘሩ ተጓዦች ${range} ኮንትራት ሳይጨርሱ መመለሳቸውን እናሳውቃለን::`;
    case EmployeeStatus.contract_completed:
      return `በዚሁም መሰረት ስማቸው ከታች የተዘረዘሩ ተጓዦች ${range} ኮንትራት ጨርሰው መመለሳቸውን እናሳውቃለን::`;
    case EmployeeStatus.discontinued:
      return `በዚሁም መሰረት ስማቸው ከታች የተዘረዘሩ ${range} ኢሹድ ተብለው ሳይበሩ በፊት ፕሮሰስ ማቋረጣቸውን እናሳውቃለን::`;
    default:
      return "";
  }
}

export const AfterFlight = ({
  employees,
  user,
  reportType,
  durationStartDate,
  durationEndDate,
  letterDate,
  letterNumber,
}: ReportProps) => {
  const validEmployees = employees.filter(
    (emp) =>
      emp.passportNumber &&
      emp.passportNumber.length > 0 &&
      emp.additionalData &&
      emp.address,
  );

  return (
    <Document
      creator="stellarsofts"
      author="pinnaSofts"
      title="EnjazPlus"
      subject="Ashara List"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image
            source={
              user?.company?.header ? user.company.header : "/noHeader.png"
            }
          />
        </View>
        <View style={{ marginTop: 120 }}>
          <View style={[styles.row2]}>
            <View style={{ width: "65%" }} />
            <View
              style={{
                width: "30%",
                borderBottomWidth: 2,
                borderColor: "black",
              }}
            >
              <Text style={styles.dateHeader}>{`ቀን: ${letterDate}`}</Text>
            </View>
            <View style={{ width: "5%" }} />
          </View>
          <View style={[styles.row2]}>
            <View style={{ width: "65%" }} />
            <View
              style={{
                width: "30%",
                borderBottomWidth: 2,
                borderColor: "black",
              }}
            >
              <Text style={styles.dateHeader}>{`ቁጥር: ${letterNumber}`}</Text>
            </View>
            <View style={{ width: "5%" }} />
          </View>
        </View>
        <View style={{ marginTop: 5 }}>
          <View style={[styles.row2, { paddingHorizontal: 20, height: 25 }]}>
            <Text style={styles.dateHeader}>ለኢትዮጲያ ፌዴራላዊ ዲሞክራሲያዊ ሪፐብሊክ</Text>
          </View>
          <View style={[styles.row2, { paddingHorizontal: 20, height: 25 }]}>
            <Text style={styles.dateHeader}>የስራና ክህሎት ሚኒስቴር</Text>
          </View>
          <View style={[styles.row2, { paddingHorizontal: 20 }]}>
            <Text
              style={[
                styles.dateHeader,
                { width: "12%", borderBottomWidth: 2, borderColor: "black" },
              ]}
            >
              አዲስ አበባ
            </Text>
          </View>
        </View>
        <View style={{ marginTop: 5 }}>
          <View style={[styles.row2]}>
            <View style={{ width: "15%" }} />
            <View style={{ width: "55%" }}>
              <Text
                style={[
                  styles.dateHeader,
                  { borderBottomWidth: 2, borderColor: "black" },
                ]}
              >
                {reportType ? (SUBJECT_LINE[reportType] ?? "") : ""}
              </Text>
            </View>
            <View style={{ width: "30%" }} />
          </View>
          <View style={[{ paddingHorizontal: 20 }]}>
            <Text
              wrap={true}
              style={[styles.dateHeader, { fontWeight: "medium" }]}
            >
              {`ድርጅታችን ${
                user?.company?.nameAmharic ?? user?.company?.displayName
              } በአዋጅ ቁጥር 923/2008 መሰረት ተቋቁሞ ወደ ስራ የገባ መሆኑ ይታወቃል::`}
            </Text>
          </View>
          <View style={[{ paddingHorizontal: 20 }]}>
            <Text
              wrap={true}
              style={[styles.dateHeader, { fontWeight: "medium" }]}
            >
              {getBodyText(reportType, durationStartDate, durationEndDate)}
            </Text>
          </View>
        </View>
        <View style={{ marginTop: 5, paddingHorizontal: 20 }}>
          <View style={[styles.row2]}>
            <View style={[styles.row21, { width: "5%" }]}>
              <Text style={styles.headerCenter}>ተ.ቁ.</Text>
            </View>
            <View style={[styles.row21, { width: "35%" }]}>
              <Text style={styles.headerCenter}>ስም</Text>
            </View>
            <View style={[styles.row21, { width: "20%" }]}>
              <Text style={styles.headerCenter}>ፓስፖርት ቁ.</Text>
            </View>
            <View style={[styles.row21, { width: "20%" }]}>
              <Text style={styles.headerCenter}>ሌበር አይዲ</Text>
            </View>
            <View style={[styles.row21, { width: "20%", borderRightWidth: 2 }]}>
              <Text style={styles.headerCenter}>
                {reportType ? (DATE_COLUMN_HEADER[reportType] ?? "") : ""}
              </Text>
            </View>
          </View>
          {validEmployees.map((employee, index) => {
            const statusDate = getEmployeeStatusDate(employee);
            const nameAmharic =
              employee.additionalData?.fullNameAmharic &&
              employee.additionalData.fullNameAmharic.length > 8
                ? employee.additionalData.fullNameAmharic
                : `${employee.givenName} ${employee.surName}`;
            const labourId =
              employee.additionalData?.labourId &&
              employee.additionalData.labourId.length > 5
                ? employee.additionalData.labourId
                : "-";
            return (
              <View style={[styles.row3]} key={employee._id ?? index}>
                <View style={[styles.row31, { width: "5%" }]}>
                  <Text style={styles.cellCenter}>{index + 1}</Text>
                </View>
                <View style={[styles.row31, { width: "35%" }]}>
                  <Text wrap={true} style={styles.cellCenter}>
                    {nameAmharic}
                  </Text>
                </View>
                <View style={[styles.row31, { width: "20%" }]}>
                  <Text style={styles.cellCenter}>
                    {employee.passportNumber}
                  </Text>
                </View>
                <View style={[styles.row31, { width: "20%" }]}>
                  <Text style={styles.cellCenter}>{labourId}</Text>
                </View>
                <View style={[styles.row31, { width: "20%" }]}>
                  <Text style={styles.cellCenter}>
                    {statusDate
                      ? getAmharicCalendarFormatted(new Date(statusDate), "/")
                      : ""}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
        <View style={{ marginTop: 10 }}>
          <View style={[styles.row2]}>
            <View style={{ width: "65%" }} />
            <View style={{ width: "35%" }}>
              <Text style={[styles.dateHeader]}>ከሠላምታ ጋር</Text>
            </View>
          </View>
          <View style={[styles.row2, { marginTop: 50 }]}>
            <View style={{ width: "65%" }} />
            <View style={{ width: "35%" }}>
              <Text style={styles.dateHeader}>
                {user?.company?.managerNameAmharic}
              </Text>
            </View>
          </View>
          <View style={[styles.row2, { marginTop: 0 }]}>
            <View style={{ width: "65%" }} />
            <View style={{ width: "35%" }}>
              <Text style={styles.dateHeader}>ዋና ስራ አስኪያጅ</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
